<template>
    <div class="spark-page">
        <!-- Loading State -->
        <div v-if="!spark" class="loading-container">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
            <p class="mt-3 text-muted">Loading Spark configuration...</p>
        </div>

        <!-- Main Content -->
        <div v-else class="spark-content">
            <!-- Page Header -->
            <div class="page-header mb-4">
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 class="mb-1">
                            <b-icon icon="lightning-charge-fill" class="text-warning mr-2"></b-icon>
                            {{ spark.name }}
                        </h2>
                        <p class="text-muted mb-0">
                            <b-icon icon="cpu" class="mr-1"></b-icon>
                            {{ spark.runtime }}
                        </p>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-button @click="save" variant="success" size="lg" class="save-btn mr-3">
                            <b-icon icon="save" class="mr-2"></b-icon>
                            {{ $t('save') }}
                        </b-button>
                        <protokol-settings-dropdown size="lg" variant="outline-secondary">
                            <b-dropdown-item-button @click="remove" variant="danger">
                                <b-icon icon="trash" class="mr-2"></b-icon>
                                {{ $t('delete') }}
                            </b-dropdown-item-button>
                        </protokol-settings-dropdown>
                    </div>
                </div>
            </div>

            <!-- Version Pipeline -->
            <div class="sp-versions">
                <div class="sp-version-stage">
                    <span class="sp-version-label">IDE</span>
                    <b-form-select v-model="previewVersion" :options="availableVersion" size="sm" class="sp-version-select"></b-form-select>
                    <b-button size="sm" variant="link" class="sp-version-action" @click="forkModal = true" v-if="spark.dev_version != ''">
                        <b-icon icon="bezier2"></b-icon>
                    </b-button>
                    <b-button size="sm" variant="link" class="text-danger sp-version-action" @click="removeVersion()" v-if="spark.dev_version != '' && canRemoveVersion">
                        <b-icon icon="trash"></b-icon>
                    </b-button>
                </div>
                <div class="sp-version-promote">
                    <b-icon icon="chevron-right" class="sp-version-arrow"></b-icon>
                </div>
                <div class="sp-version-stage">
                    <span class="sp-version-label">Dev</span>
                    <b-form-select v-model="devVersion" @change="publishDevVersion($event)" :options="versionOptionsWithEmpty" size="sm" class="sp-version-select">
                        <template #first>
                            <b-form-select-option :value="null" disabled>{{ devVersion }}</b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-button v-if="spark.dev_version" size="sm" variant="link" class="sp-version-action" @click="redeployDevVersion">
                        <b-icon icon="arrow-clockwise"></b-icon>
                    </b-button>
                    <span class="sp-version-chip" :class="spark.dev_version ? 'sp-version-chip--success' : 'sp-version-chip--inactive'">
                        <b-icon :icon="spark.dev_version ? 'check-circle-fill' : 'dash-circle'" class="sp-version-chip-icon"></b-icon>
                        {{ spark.dev_version || $t('notActive') }}
                    </span>
                </div>
                <div class="sp-version-promote">
                    <b-icon icon="chevron-right" class="sp-version-arrow"></b-icon>
                </div>
                <div class="sp-version-stage">
                    <span class="sp-version-label">Live</span>
                    <b-form-select v-model="liveVersion" @change="publishLiveVersion($event)" :options="versionOptionsWithEmpty" size="sm" class="sp-version-select">
                        <template #first>
                            <b-form-select-option :value="null" disabled>{{ liveVersion }}</b-form-select-option>
                        </template>
                    </b-form-select>
                    <b-button v-if="spark.public_version" size="sm" variant="link" class="sp-version-action" @click="redeployLiveVersion">
                        <b-icon icon="arrow-clockwise"></b-icon>
                    </b-button>
                    <span class="sp-version-chip" :class="spark.public_version ? 'sp-version-chip--live' : 'sp-version-chip--inactive'">
                        <b-icon :icon="spark.public_version ? 'globe2' : 'dash-circle'" class="sp-version-chip-icon"></b-icon>
                        {{ spark.public_version || $t('notPublished') }}
                    </span>
                </div>
            </div>

            <!-- Tabs -->
            <b-tabs content-class="mt-4" nav-class="spark-tabs" @input="onTabChange">
                <!-- General Tab -->
                <b-tab active>
                    <template #title>
                        <b-icon icon="gear-fill" class="mr-2"></b-icon>
                        {{ $t('general') }}
                    </template>

                    <b-row>
                        <!-- Configuration Column -->
                        <b-col cols="12" md="12" class="mb-4">
                            <!-- Basic Configuration Card -->
                            <b-card class="config-card shadow-sm mb-4">
                                <template #header>
                                    <h5 class="mb-0">
                                        <b-icon icon="sliders" class="mr-2"></b-icon>
                                        Configuration
                                    </h5>
                                </template>

                                <b-form-group
                                    :label="$t('memory')"
                                    :description="$t('sparkMemoryDescription')"
                                    label-class="font-weight-semibold"
                                >
                                    <div class="d-flex align-items-center">
                                        <v-select 
                                            :options="availableMemory" 
                                            v-model="spark.memory"
                                            :clearable="false"
                                            :disabled="memoryLocked"
                                            @input="onMemoryChange"
                                            :reduce="option => option.value"
                                            label="label"
                                            class="custom-v-select flex-grow-1"
                                        />
                                        <b-button
                                            :variant="memoryLocked ? 'outline-warning' : 'outline-success'"
                                            size="sm"
                                            @click="toggleMemoryLock"
                                            class="ml-2"
                                        >
                                            <b-icon :icon="memoryLocked ? 'lock-fill' : 'unlock-fill'"></b-icon>
                                        </b-button>
                                    </div>
                                    <small class="text-muted mt-2 d-block">
                                        <b-icon icon="info-circle" class="mr-1"></b-icon>
                                        {{ $t('memoryChangeAutoRedeploy') }}
                                    </small>
                                </b-form-group>
                            </b-card>

                            <!-- Endpoints Card -->
                            <b-card class="endpoints-card shadow-sm">
                                <template #header>
                                    <div class="d-flex justify-content-between align-items-center">
                                        <h5 class="mb-0">
                                            <b-icon icon="link-45deg" class="mr-2"></b-icon>
                                            Endpoints
                                        </h5>
                                        <div class="d-flex align-items-center gap-2">
                                            <b-badge v-if="isBuilding" variant="warning" pill class="pulse-badge">
                                                <b-spinner small class="mr-1"></b-spinner>
                                                Building
                                            </b-badge>
                                            <b-button 
                                                v-if="isBuilding"
                                                variant="outline-danger" 
                                                size="sm"
                                                @click="cancelDeployment"
                                                class="ml-2"
                                            >
                                                <b-icon icon="x-circle"></b-icon>
                                                {{ $t('cancel') }}
                                            </b-button>
                                        </div>
                                    </div>
                                </template>

                                <!-- Dev Endpoint -->
                                <b-form-group v-if="spark.dev_version" label="Development" label-class="font-weight-semibold" class="mb-3">
                                    <div class="d-flex align-items-center mb-2">
                                        <b-badge :variant="devStatusVariant" pill class="status-badge">
                                            <b-spinner v-if="devStatus === 'building'" small class="mr-1"></b-spinner>
                                            <b-icon v-else-if="devStatus === 'ready'" icon="check-circle-fill" class="mr-1"></b-icon>
                                            <b-icon v-else icon="exclamation-circle-fill" class="mr-1"></b-icon>
                                            {{ devStatusText }}
                                        </b-badge>
                                    </div>
                                    <b-input-group>
                                        <b-form-input :value="devEndpoint" readonly class="endpoint-input"></b-form-input>
                                        <b-input-group-append>
                                            <b-button variant="outline-secondary" @click="copyToClipboard(devEndpoint)">
                                                <b-icon icon="clipboard"></b-icon>
                                            </b-button>
                                            <b-button variant="primary" @click="testEndpoint(devEndpoint)" :disabled="devStatus !== 'ready'">
                                                <b-icon icon="play-fill"></b-icon>
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                </b-form-group>

                                <!-- Live Endpoint -->
                                <b-form-group v-if="spark.public_version" label="Production" label-class="font-weight-semibold" class="mb-0">
                                    <div class="d-flex align-items-center mb-2">
                                        <b-badge :variant="liveStatusVariant" pill class="status-badge">
                                            <b-spinner v-if="liveStatus === 'building'" small class="mr-1"></b-spinner>
                                            <b-icon v-else-if="liveStatus === 'ready'" icon="check-circle-fill" class="mr-1"></b-icon>
                                            <b-icon v-else icon="exclamation-circle-fill" class="mr-1"></b-icon>
                                            {{ liveStatusText }}
                                        </b-badge>
                                    </div>
                                    <b-input-group>
                                        <b-form-input :value="liveEndpoint" readonly class="endpoint-input"></b-form-input>
                                        <b-input-group-append>
                                            <b-button variant="outline-secondary" @click="copyToClipboard(liveEndpoint)">
                                                <b-icon icon="clipboard"></b-icon>
                                            </b-button>
                                            <b-button variant="primary" @click="testEndpoint(liveEndpoint)" :disabled="liveStatus !== 'ready'">
                                                <b-icon icon="play-fill"></b-icon>
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                </b-form-group>

                                <b-alert v-if="!spark.dev_version && !spark.public_version" show variant="secondary" class="mb-0">
                                    <small>No active endpoints. Publish a version to get endpoint URLs.</small>
                                </b-alert>
                            </b-card>
                        </b-col>
                    </b-row>
                </b-tab>

                <!-- Code Tab -->
                <b-tab>
                    <template #title>
                        <b-icon icon="file-code" class="mr-2"></b-icon>
                        Code
                    </template>

                    <b-row>
                        <!-- Handler Editor -->
                        <b-col cols="12" xl="6" class="mb-4">
                            <b-card v-if="handlerExpression" class="code-card shadow-sm h-100">
                                <template #header>
                                    <div class="d-flex justify-content-between align-items-center">
                                        <h5 class="mb-0">
                                            <b-icon icon="file-code" class="mr-2"></b-icon>
                                            Handler
                                        </h5>
                                        <b-badge v-if="ideVersion == liveVersion" variant="warning" pill>
                                            <b-icon icon="lock-fill" class="mr-1"></b-icon>
                                            Read Only
                                        </b-badge>
                                    </div>
                                </template>
                                <CodeEditor 
                                    v-model="handlerExpression" 
                                    :withButton="false" 
                                    :readOnly="ideVersion == liveVersion" 
                                    :controls="false"
                                    :height="700"
                                />
                            </b-card>
                        </b-col>

                        <!-- Package Editor -->
                        <b-col cols="12" xl="6" class="mb-4">
                            <b-card v-if="packageExpression" class="code-card shadow-sm h-100">
                                <template #header>
                                    <div class="d-flex justify-content-between align-items-center">
                                        <h5 class="mb-0">
                                            <b-icon icon="box-seam" class="mr-2"></b-icon>
                                            Package
                                        </h5>
                                        <b-badge v-if="ideVersion == liveVersion" variant="warning" pill>
                                            <b-icon icon="lock-fill" class="mr-1"></b-icon>
                                            Read Only
                                        </b-badge>
                                    </div>
                                </template>
                                <CodeEditor 
                                    v-model="packageExpression" 
                                    :withButton="false" 
                                    :readOnly="ideVersion == liveVersion" 
                                    :controls="false"
                                    :height="700"
                                />
                            </b-card>
                        </b-col>
                    </b-row>
                </b-tab>

                <!-- Environment Tab -->
                <b-tab>
                    <template #title>
                        <b-icon icon="server" class="mr-2"></b-icon>
                        {{ $t('environment') }}
                    </template>

                    <b-row>
                        <!-- Environment Selector -->
                        <b-col cols="12" class="mb-4">
                            <b-card class="env-selector-card shadow-sm">
                                <div class="d-flex align-items-center justify-content-between">
                                    <label class="font-weight-semibold mb-0">
                                        <b-icon icon="hdd-rack" class="mr-2"></b-icon>
                                        {{ $t('environment') }}
                                    </label>
                                    <b-button-group>
                                        <b-button 
                                            :variant="env === 'dev' ? 'primary' : 'outline-primary'" 
                                            @click="env = 'dev'"
                                            class="env-toggle"
                                        >
                                            <b-icon icon="code-slash" class="mr-1"></b-icon>
                                            Dev
                                        </b-button>
                                        <b-button 
                                            :variant="env === 'live' ? 'primary' : 'outline-primary'" 
                                            @click="env = 'live'"
                                            class="env-toggle"
                                        >
                                            <b-icon icon="broadcast" class="mr-1"></b-icon>
                                            Live
                                        </b-button>
                                    </b-button-group>
                                </div>
                            </b-card>
                        </b-col>

                        <!-- Environment Variables Editor -->
                        <b-col cols="12">
                            <b-card class="env-card shadow-sm">
                                <template #header>
                                    <div class="d-flex justify-content-between align-items-center">
                                        <h6 class="mb-0">
                                            <b-icon icon="list-task" class="mr-2"></b-icon>
                                            {{ $t('envVariables') }}
                                        </h6>
                                        <div>
                                            <b-button variant="primary" @click="addEnvVariable()" size="sm" class="mr-2">
                                                <b-icon icon="plus-circle" class="mr-1"></b-icon>
                                                {{ $t('add') }}
                                            </b-button>
                                            <b-button variant="success" @click="save()" size="sm">
                                                <b-icon icon="save" class="mr-1"></b-icon>
                                                {{ $t('save') }}
                                            </b-button>
                                        </div>
                                    </div>
                                </template>
                                
                                <div class="env-variables-list">
                                    <div 
                                        v-for="(variable, index) in envVariablesList" 
                                        :key="index" 
                                        class="env-variable-row"
                                    >
                                        <b-form-group class="env-key-group mb-0">
                                            <b-form-input
                                                v-model="variable.key"
                                                placeholder="Key"
                                                class="env-input"
                                                @blur="updateEnvVariables"
                                            />
                                        </b-form-group>
                                        
                                        <b-form-group class="env-value-group mb-0">
                                            <b-form-input
                                                v-model="variable.value"
                                                placeholder="Value"
                                                class="env-input"
                                                @blur="updateEnvVariables"
                                            />
                                        </b-form-group>
                                        
                                        <b-button 
                                            variant="outline-danger" 
                                            size="sm" 
                                            @click="removeEnvVariable(index)"
                                            class="env-delete-btn"
                                        >
                                            <b-icon icon="trash"></b-icon>
                                        </b-button>
                                    </div>
                                    
                                    <div v-if="envVariablesList.length === 0" class="text-center text-muted py-4">
                                        <b-icon icon="inbox" font-scale="2" class="mb-2"></b-icon>
                                        <p class="mb-0">{{ $t('noEnvVariables') }}</p>
                                        <small>{{ $t('clickAddToCreate') }}</small>
                                    </div>
                                </div>
                            </b-card>
                        </b-col>
                    </b-row>
                </b-tab>

                <!-- Logs Tab -->
                <b-tab>
                    <template #title>
                        <b-icon icon="terminal" class="mr-2"></b-icon>
                        Logs
                    </template>

                    <b-row>
                        <b-col cols="12">
                            <!-- Environment Selector -->
                            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
                                <b-button-group>
                                    <b-button 
                                        :variant="logEnv === 'dev' ? 'primary' : 'outline-primary'"
                                        @click="switchLogEnv('dev')"
                                    >
                                        <b-icon icon="code-square" class="mr-1"></b-icon>
                                        Dev
                                    </b-button>
                                    <b-button 
                                        :variant="logEnv === 'live' ? 'primary' : 'outline-primary'"
                                        @click="switchLogEnv('live')"
                                    >
                                        <b-icon icon="globe" class="mr-1"></b-icon>
                                        Live
                                    </b-button>
                                </b-button-group>

                                <div class="d-flex align-items-center gap-2">
                                    <b-badge 
                                        :variant="logPollingActive ? 'success' : 'secondary'" 
                                        pill
                                        class="px-3 py-2 clickable-badge mr-2"
                                        @click="toggleLogPolling"
                                    >
                                        <b-spinner v-if="logPollingActive" small class="mr-1"></b-spinner>
                                        <b-icon v-else icon="circle-fill" class="mr-1"></b-icon>
                                        {{ logPollingActive ? 'Live' : 'Paused' }}
                                    </b-badge>
                                    <b-button 
                                        variant="outline-danger" 
                                        size="sm"
                                        @click="clearLogs"
                                    >
                                        <b-icon icon="trash"></b-icon>
                                        Clear
                                    </b-button>
                                </div>
                            </div>

                            <!-- Logs Display -->
                            <b-card class="logs-card shadow-sm">
                                <template #header>
                                    <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                        <h5 class="mb-0">
                                            <b-icon icon="terminal" class="mr-2"></b-icon>
                                            {{ logEnv === 'dev' ? 'Development' : 'Live' }} Logs
                                        </h5>
                                        <div class="d-flex align-items-center gap-2 flex-wrap">
                                            <div class="d-flex align-items-center">
                                                <span class="text-muted mr-2" style="font-size: 0.95rem; font-weight: 500;">Since:</span>
                                                <div class="date-picker-wrapper">
                                                    <b-form-group class="mb-0">
                                                        <b-input-group size="sm">
                                                            <b-input-group-prepend>
                                                                <b-input-group-text>
                                                                    <b-icon icon="calendar3"></b-icon>
                                                                </b-input-group-text>
                                                            </b-input-group-prepend>
                                                            <b-form-input
                                                                v-model="manualSince"
                                                                type="datetime-local"
                                                                @change="onManualSinceChange"
                                                                :max="getCurrentDateTime()"
                                                                placeholder="Select start time"
                                                            />
                                                            <b-input-group-append v-if="manualSince">
                                                                <b-button 
                                                                    variant="outline-secondary" 
                                                                    size="sm"
                                                                    @click="resetManualSince"
                                                                >
                                                                    <b-icon icon="x"></b-icon>
                                                                </b-button>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                    </b-form-group>
                                                </div>
                                            </div>
                                            <div class="log-search-wrapper ml-3">
                                                <b-input-group size="sm">
                                                    <b-input-group-prepend>
                                                        <b-input-group-text>
                                                            <b-icon icon="search"></b-icon>
                                                        </b-input-group-text>
                                                    </b-input-group-prepend>
                                                    <b-form-input
                                                        v-model="logSearchQuery"
                                                        placeholder="Search logs..."
                                                        debounce="300"
                                                    />
                                                    <b-input-group-append v-if="logSearchQuery">
                                                        <b-button 
                                                            variant="outline-secondary" 
                                                            size="sm"
                                                            @click="logSearchQuery = ''"
                                                        >
                                                            <b-icon icon="x"></b-icon>
                                                        </b-button>
                                                    </b-input-group-append>
                                                </b-input-group>
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <div class="logs-container">
                                    <div v-if="filteredLogs.length === 0 && logs.length === 0" class="text-center text-muted py-5">
                                        <b-icon icon="inbox" font-scale="3" class="mb-3"></b-icon>
                                        <p>No logs available</p>
                                        <small>Logs will appear here when your Spark function is invoked</small>
                                    </div>
                                    <div v-else-if="filteredLogs.length === 0 && logs.length > 0" class="text-center text-muted py-5">
                                        <b-icon icon="search" font-scale="3" class="mb-3"></b-icon>
                                        <p>No logs match your search</p>
                                        <small>Try a different search term</small>
                                    </div>
                                    <div v-else class="logs-list">
                                        <div 
                                            v-for="(log, index) in filteredLogs" 
                                            :key="index" 
                                            class="log-entry"
                                            :class="['log-level-' + getSeverityClass(log.level), log.data ? 'log-clickable' : '']"
                                            @click="log.data ? inspectLog(log) : null"
                                        >
                                            <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
                                            <span class="log-level">{{ log.level || 'INFO' }}</span>
                                            <span class="log-message">{{ log.message }}</span>
                                            <b-icon v-if="log.data" icon="eye" class="ml-2 text-primary"></b-icon>
                                        </div>
                                    </div>
                                </div>
                            </b-card>
                        </b-col>
                    </b-row>
                </b-tab>

                <!-- IDL Tab -->
                <b-tab>
                    <template #title>
                        <b-icon icon="file-code" class="mr-2"></b-icon>
                        {{ $t('idl.title') }}
                    </template>

                    <b-card class="shadow-sm">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <p class="text-muted mb-0">{{ $t('idl.description') }}</p>
                            <a href="https://docs.protokol.io/components/idl/" target="_blank" rel="noopener" class="text-nowrap ml-3">
                                <b-icon icon="book" class="mr-1"></b-icon><small>{{ $t('documentation') }}</small>
                            </a>
                        </div>
                        <CodeEditor lang="application/json" :withButton="false" v-model="sparkIdlJson" :height="600" />
                    </b-card>
                </b-tab>
            </b-tabs>

            <!-- Fork Modal -->
            <main-dialog 
                v-model="forkModal" 
                :title="$t('fork') + ' ' + $t('version')"
                :ok-title="$t('fork')" 
                @ok="forkVersion"
                ok-variant="info"
                centered
            >
                <b-form-group :label="$t('version')" label-class="font-weight-semibold">
                    <b-input v-model="previewVersion" disabled class="bg-light"></b-input>
                </b-form-group>

                <div class="text-center my-3">
                    <b-icon icon="arrow-down" font-scale="2" class="text-muted"></b-icon>
                </div>

                <b-form-group :label="$t('newVersion')" label-class="font-weight-semibold">
                    <b-input v-model="forkTo" placeholder="Enter new version name"></b-input>
                </b-form-group>
            </main-dialog>

            <!-- Test Modal -->
            <main-dialog 
                v-model="testModal" 
                title="Test Spark Endpoint"
                size="xl"
                hide-footer
                centered
            >
                <b-row>
                    <!-- Request Section -->
                    <b-col cols="12" md="6" class="mb-4">
                        <h5 class="mb-3">
                            <b-icon icon="send" class="mr-2"></b-icon>
                            Request
                        </h5>

                        <b-form-group label="Method" label-class="font-weight-semibold">
                            <b-form-select v-model="testMethod" class="test-select">
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                                <option value="DELETE">DELETE</option>
                                <option value="PATCH">PATCH</option>
                            </b-form-select>
                        </b-form-group>

                        <b-form-group label="Endpoint" label-class="font-weight-semibold">
                            <b-form-input v-model="testEndpointUrl" readonly class="endpoint-input"></b-form-input>
                        </b-form-group>

                        <b-form-group v-if="testMethod !== 'GET'" label="Request Body (JSON)" label-class="font-weight-semibold">
                            <CodeEditor 
                                v-model="testRequestBody" 
                                :withButton="false" 
                                :controls="false"
                                :height="300"
                                lang="application/json"
                            />
                        </b-form-group>

                        <b-button 
                            variant="primary" 
                            @click="executeTest" 
                            :disabled="testLoading"
                            block
                            size="lg"
                        >
                            <b-spinner v-if="testLoading" small class="mr-2"></b-spinner>
                            <b-icon v-else icon="play-fill" class="mr-2"></b-icon>
                            {{ testLoading ? 'Executing...' : 'Execute Request' }}
                        </b-button>
                    </b-col>

                    <!-- Response Section -->
                    <b-col cols="12" md="6">
                        <h5 class="mb-3">
                            <b-icon icon="reply" class="mr-2"></b-icon>
                            Response
                        </h5>

                        <div v-if="testError" class="test-error">
                            <b-alert show variant="danger">
                                <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                                {{ testError }}
                            </b-alert>
                        </div>

                        <div v-else-if="testResponse" class="test-response">
                            <b-form-group label="Status" label-class="font-weight-semibold">
                                <b-badge :variant="testResponse.status < 400 ? 'success' : 'danger'" class="status-code-badge">
                                    {{ testResponse.status }} {{ testResponse.statusText }}
                                </b-badge>
                            </b-form-group>

                            <b-form-group label="Response Body" label-class="font-weight-semibold">
                                <CodeEditor 
                                    :value="JSON.stringify(testResponse.data, null, 2)" 
                                    :withButton="false" 
                                    :controls="false"
                                    :height="350"
                                    :readOnly="true"
                                    lang="application/json"
                                />
                            </b-form-group>
                        </div>

                        <div v-else class="test-placeholder">
                            <div class="text-center text-muted py-5">
                                <b-icon icon="arrow-left" font-scale="3" class="mb-3"></b-icon>
                                <p>Execute a request to see the response</p>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </main-dialog>

            <!-- Log Inspection Modal -->
            <main-dialog 
                v-model="inspectModal" 
                title="Log Data Inspector"
                size="xl"
                ok-only
                ok-title="Close"
                centered
            >
                <div v-if="selectedLog">
                    <b-row class="mb-3">
                        <b-col cols="12" md="6">
                            <b-form-group label="Timestamp" label-class="font-weight-semibold">
                                <b-form-input :value="selectedLog.timestamp" readonly class="bg-light"></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group label="Level" label-class="font-weight-semibold">
                                <b-badge 
                                    :variant="selectedLog.level === 'error' ? 'danger' : selectedLog.level === 'warn' ? 'warning' : 'info'" 
                                    class="px-3 py-2"
                                    style="font-size: 1rem;"
                                >
                                    {{ selectedLog.level.toUpperCase() }}
                                </b-badge>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-form-group label="Message" label-class="font-weight-semibold">
                        <b-form-textarea :value="selectedLog.message" readonly rows="2" class="bg-light"></b-form-textarea>
                    </b-form-group>

                    <b-form-group v-if="selectedLog.data" label="Data" label-class="font-weight-semibold">
                        <CodeEditor 
                            :value="JSON.stringify(selectedLog.data, null, 2)" 
                            :withButton="false" 
                            :controls="false"
                            :height="500"
                            :readOnly="true"
                            lang="application/json"
                        />
                    </b-form-group>

                    <b-alert v-else show variant="info">
                        <b-icon icon="info-circle" class="mr-2"></b-icon>
                        No additional data available for this log entry.
                    </b-alert>
                </div>
            </main-dialog>
        </div>
    </div>
</template>
<script>
import CodeEditor from '@/components/form/Editor/Code'
import { get, cloneDeep } from 'lodash'
import VSelect from 'vue-select'

export default {
    name: 'Spark',
    components: {
        CodeEditor,
        VSelect,
    },
    data: function() {
        return {
            spark: null,
            ideVersion: this.previewVersion || "Choose",
            forkModal: false,
            forkTo: null,
            memory: null,
            env: "dev",
            logs: [],
            logInterval: null,
            pollInterval: null,
            logEnv: 'dev',
            logPollingActive: false,
            logSince: null,
            manualSince: null,
            logSearchQuery: '',
            availableMemory: [
                { label: '128 MB', value: 128 },
                { label: '256 MB', value: 256 },
                { label: '512 MB', value: 512 },
                { label: '1 GB', value: 1024 }
            ],
            memoryLocked: true,
            previousMemory: null,
            testModal: false,
            testEndpointUrl: '',
            testMethod: 'POST',
            testRequestBody: '{}',
            testResponse: null,
            testLoading: false,
            testError: null,
            activeTabIndex: 0,
            selectedLog: null,
            inspectModal: false,
        }
    },
    computed: {
        filteredLogs() {
            if (!this.logSearchQuery || this.logSearchQuery.trim() === '') {
                return this.logs
            }

            const query = this.logSearchQuery.toLowerCase()

            return this.logs.filter(log => {
                // Search in message
                if (log.message && log.message.toLowerCase().includes(query)) {
                    return true
                }

                // Search in data (convert to JSON string for searching)
                if (log.data) {
                    try {
                        const dataStr = JSON.stringify(log.data).toLowerCase()
                        if (dataStr.includes(query)) {
                            return true
                        }
                    } catch (e) {
                        // If JSON stringification fails, skip data search
                    }
                }

                return false
            })
        },

        availableVersion() {
            return this.spark.settings.map(s => {
                return s.version
            })
        },

        versionOptionsWithEmpty() {
            return [
                { value: '', text: this.$t('deactivate') },
                ...this.availableVersion.map(v => ({ value: v, text: v }))
            ]
        },

        canRemoveVersion() {
            return this.spark.settings.length > 1
        },

        previewVersion: {
            get() {
                return this.ideVersion
            },

            set(val) {
                this.ideVersion = val
            }
        },

        liveVersion: {
            get() {
                return this.spark.public_version || this.$t('notPublished')
            },
            set(val) {
                // Handled by @change event
            }
        },

        devVersion: {
            get() {
                return this.spark.dev_version || this.$t('notActive')
            },
            set(val) {
                // Handled by @change event
            }
        },

        handlerExpression: {
            get() {
                let settings = this.spark.settings.find(s => {
                    return s.version == this.ideVersion
                })

                return get(settings, 'dist.handler', null)
            },

            set(val) {
                let settings = this.spark.settings.find(s => {
                    return s.version == this.ideVersion
                })

                settings.dist.handler = val
            }
        },

        packageExpression: {
            get() {
                let settings = this.spark.settings.find(s => {
                    return s.version == this.ideVersion
                })

                return get(settings, 'dist.package', null)
            },

            set(val) {
                let settings = this.spark.settings.find(s => {
                    return s.version == this.ideVersion
                })

                settings.dist.package = val
            }
        },

        envVariables: {
            get() {
                return JSON.stringify(get(this.spark.info, `${this.env}.env_vars`, {}) || {}, null, 4)
            },

            set(vars) {
                vars = JSON.parse(vars)
                this.spark.info[this.env].env_vars = vars
            }
        },

        envVariablesList() {
            const envVars = get(this.spark.info, `${this.env}.env_vars`, {}) || {}
            return Object.entries(envVars).map(([key, value]) => ({
                key,
                value
            }))
        },

        devEndpoint() {
            if (!this.spark.dev_version) return ''
            const baseUrl = window.__ENV_VARIABLES__.SPARK_URL || 'https://spark.protokol.io'
            return `${baseUrl}/${this.spark.name}`
        },

        liveEndpoint() {
            if (!this.spark.public_version) return ''
            const baseUrl = window.__ENV_VARIABLES__.SPARK_URL || 'https://spark.protokol.io'
            return `${baseUrl}/${this.spark.name}`
        },

        sparkIdlJson: {
            get() {
                return JSON.stringify(this.spark && this.spark.idl ? this.spark.idl : {}, null, 2)
            },
            set(v) {
                try {
                    this.$set(this.spark, 'idl', JSON.parse(v))
                } catch (e) { /* ignore invalid JSON */ }
            }
        },

        devStatus() {
            return (this.spark.info && this.spark.info.dev && this.spark.info.dev.status) || ''
        },

        liveStatus() {
            return (this.spark.info && this.spark.info.live && this.spark.info.live.status) || ''
        },

        isBuilding() {
            return this.devStatus === 'building' || this.liveStatus === 'building' || this.devStatus === 'deploying' || this.liveStatus === 'deploying'
        },

        devStatusVariant() {
            if (this.devStatus === 'ready') return 'success'
            if (this.devStatus === 'building') return 'warning'
            if (this.devStatus === 'deploying') return 'warning'
            if (this.devStatus === 'failed') return 'danger'
            return 'secondary'
        },

        liveStatusVariant() {
            if (this.liveStatus === 'ready') return 'success'
            if (this.liveStatus === 'building') return 'warning'
            if (this.liveStatus === 'deploying') return 'warning'
            if (this.liveStatus === 'failed') return 'danger'
            return 'secondary'
        },

        devStatusText() {
            if (this.devStatus === 'ready') return 'Ready'
            if (this.devStatus === 'building') return 'Building'
            if (this.devStatus === 'deploying') return 'Deploying'
            if (this.devStatus === 'failed') return 'Failed'
            return 'Not Deployed'
        },

        liveStatusText() {
            if (this.liveStatus === 'ready') return 'Ready'
            if (this.liveStatus === 'building') return 'Building'
            if (this.liveStatus === 'deploying') return 'Deploying'
            if (this.liveStatus === 'failed') return 'Failed'
            return 'Not Deployed'
        }
    },
    methods: {
        removeVersion() {},

        toggleMemoryLock() {
            this.memoryLocked = !this.memoryLocked
            if (this.memoryLocked) {
                // If locking without changes, just lock it
                this.previousMemory = this.spark.memory
            } else {
                // Unlocking - store current value
                this.previousMemory = this.spark.memory
            }
        },

        async onMemoryChange(newMemory) {
            if (this.memoryLocked || !this.previousMemory) {
                return
            }

            // Check if memory actually changed
            if (newMemory === this.previousMemory) {
                return
            }

            // Confirm the change
            const confirmed = await this.$mainDialog.msgBoxConfirm(
                this.$t('memoryChangeConfirmMessage', { from: this.previousMemory, to: newMemory }),
                {
                    title: this.$t('memoryChangeConfirmTitle'),
                    okVariant: 'warning',
                    okTitle: this.$t('memoryChangeAndRedeploy'),
                    cancelTitle: this.$t('cancel')
                }
            )

            if (!confirmed) {
                // Revert the change
                this.spark.memory = this.previousMemory
                return
            }

            // Save the spark configuration
            const popup = this.$loadingPopup()
            try {
                await this.$sandbox.spark().update(this.$route.params.id, this.spark)
                
                this.$bvToast.toast(this.$t('memoryConfigSaved'), {
                    title: this.$t('success'),
                    variant: 'success',
                    autoHideDelay: 3000
                })

                // Update previous memory
                this.previousMemory = newMemory
                
                // Lock the field again
                this.memoryLocked = true

                // Trigger redeployment for active environments
                const redeployPromises = []
                
                if (this.spark.dev_version) {
                    redeployPromises.push(
                        this.$sandbox.spark().deploy(this.$route.params.id, this.spark.dev_version)
                            .then(() => {
                                this.$bvToast.toast(this.$t('devRedeploymentStarted'), {
                                    title: 'Redeploying',
                                    variant: 'info',
                                    autoHideDelay: 3000
                                })
                            })
                    )
                }

                if (this.spark.public_version) {
                    redeployPromises.push(
                        this.$sandbox.spark().release(this.$route.params.id, this.spark.public_version)
                            .then(() => {
                                this.$bvToast.toast(this.$t('liveRedeploymentStarted'), {
                                    title: 'Redeploying',
                                    variant: 'info',
                                    autoHideDelay: 3000
                                })
                            })
                    )
                }

                // Wait for all redeployments to start
                if (redeployPromises.length > 0) {
                    await Promise.all(redeployPromises)
                    // Fetch fresh status and start polling
                    await this.fetchSparkStatus()
                    this.startPolling()
                } else {
                    this.$bvToast.toast(this.$t('noActiveEnvironments'), {
                        title: 'Info',
                        variant: 'info',
                        autoHideDelay: 3000
                    })
                }
            } catch (err) {
                console.error('Error updating memory:', err)
                let errorMessage = this.$t('unexpectedError')
                if (err.response && err.response.data && err.response.data.message) {
                    errorMessage = err.response.data.message
                }
                this.$bvToast.toast(errorMessage, {
                    title: this.$t('error'),
                    variant: 'danger',
                    autoHideDelay: 5000
                })
                // Revert the change on error
                this.spark.memory = this.previousMemory
            } finally {
                popup.hide()
            }
        },

        async publishLiveVersion(version) {
            let previousVersion = this.spark.public_version

            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return
            
            const popup = this.$loadingPopup()

            this.spark.public_version = version
            if (version == '') {
                version = 'none'
            }

            this.$sandbox.spark().release(this.$route.params.id, version)
            .then(() => {
                // Fetch fresh spark data to get updated statuses
                return this.fetchSparkStatus()
            })
            .catch(err => {
                console.error('Full error object:', err)
                console.error('Error response:', err.response)
                console.error('Error response data:', err.response && err.response.data)

                let errorMessage = this.$t('unexpectedError')
                if (err.response && err.response.data) {
                    if (err.response.data.message) {
                        errorMessage = err.response.data.message
                    } else if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data
                    }
                } else if (err.message) {
                    errorMessage = err.message
                }

                this.$bvToast.toast(errorMessage, {
                    title: this.$t('error'),
                    variant: 'danger',
                    autoHideDelay: 5000
                })
                this.spark.public_version = previousVersion
            }).finally(() => {
                popup.hide()
            })
        },
        
        async publishDevVersion(version) {
            let previousVersion = this.spark.dev_version
            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return

            const popup = this.$loadingPopup()
            this.spark.dev_version = version
            if (version == '') {
                version = 'none'
            }
            this.$sandbox.spark().deploy(this.$route.params.id, version)
            .then(() => {
                // Fetch fresh spark data to get updated statuses
                return this.fetchSparkStatus()
            })
            .then(() => {
                // Start polling to check deployment status
                this.startPolling()
            })
            .catch(err => {
                console.error('Full error object:', err)
                console.error('Error response:', err.response)
                console.error('Error response data:', err.response && err.response.data)

                let errorMessage = this.$t('unexpectedError')
                if (err.response && err.response.data) {
                    if (err.response.data.message) {
                        errorMessage = err.response.data.message
                    } else if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data
                    }
                } else if (err.message) {
                    errorMessage = err.message
                }

                this.$bvToast.toast(errorMessage, {
                    title: this.$t('error'),
                    variant: 'danger',
                    autoHideDelay: 5000
                })
                this.spark.dev_version = previousVersion
            }).finally(() => {
                popup.hide()
            })
        },

        async redeployDevVersion() {
            if (!this.spark.dev_version) return

            let confirm = await this.$mainDialog.msgBoxConfirm(
                this.$t('redeployConfirmMessage', { version: this.spark.dev_version }), 
                {
                    title: this.$t('redeploy'),
                    okVariant: 'primary',
                    okTitle: this.$t('redeploy'),
                    cancelTitle: this.$t('cancel')
                }
            )
            if (!confirm) return

            const popup = this.$loadingPopup()
            this.$sandbox.spark().deploy(this.$route.params.id, this.spark.dev_version)
            .then(() => {
                this.$bvToast.toast(this.$t('redeploySuccess'), {
                    title: this.$t('success'),
                    variant: 'success',
                })
                // Fetch fresh spark data to get updated statuses
                return this.fetchSparkStatus()
            })
            .then(() => {
                // Start polling to check deployment status
                this.startPolling()
            })
            .catch(err => {
                console.error('Redeploy error:', err)
                console.error('Error response:', err.response)

                let errorMessage = this.$t('unexpectedError')
                if (err.response && err.response.data) {
                    if (err.response.data.message) {
                        errorMessage = err.response.data.message
                    } else if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data
                    }
                } else if (err.message) {
                    errorMessage = err.message
                }

                this.$bvToast.toast(errorMessage, {
                    title: this.$t('error'),
                    variant: 'danger',
                    autoHideDelay: 5000
                })
            }).finally(() => {
                popup.hide()
            })
        },

        async redeployLiveVersion() {
            if (!this.spark.public_version) return

            let confirm = await this.$mainDialog.msgBoxConfirm(
                this.$t('redeployConfirmMessage', { version: this.spark.public_version }), 
                {
                    title: this.$t('redeploy'),
                    okVariant: 'primary',
                    okTitle: this.$t('redeploy'),
                    cancelTitle: this.$t('cancel')
                }
            )
            if (!confirm) return

            const popup = this.$loadingPopup()
            this.$sandbox.spark().release(this.$route.params.id, this.spark.public_version)
            .then(() => {
                this.$bvToast.toast(this.$t('redeploySuccess'), {
                    title: this.$t('success'),
                    variant: 'success',
                })
                // Fetch fresh spark data to get updated statuses
                return this.fetchSparkStatus()
            })
            .then(() => {
                // Start polling to check deployment status
                this.startPolling()
            })
            .catch(err => {
                console.error('Full error object:', err)
                console.error('Error response:', err.response)
                console.error('Error response data:', err.response && err.response.data)

                let errorMessage = this.$t('unexpectedError')
                if (err.response && err.response.data) {
                    if (err.response.data.message) {
                        errorMessage = err.response.data.message
                    } else if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data
                    }
                } else if (err.message) {
                    errorMessage = err.message
                }

                this.$bvToast.toast(errorMessage, {
                    title: this.$t('error'),
                    variant: 'danger',
                    autoHideDelay: 5000
                })
            }).finally(() => {
                popup.hide()
            })
        },

        async cancelDeployment() {
            let confirm = await this.$mainDialog.msgBoxConfirm(
                this.$t('cancelDeploymentConfirmMessage'), 
                {
                    title: this.$t('cancelDeployment'),
                    okVariant: 'danger',
                    okTitle: this.$t('cancel'),
                    cancelTitle: this.$t('goBack')
                }
            )
            if (!confirm) return

            const popup = this.$loadingPopup()
            this.$sandbox.spark().cancel(this.$route.params.id)
            .then(() => {
                this.$bvToast.toast(this.$t('deploymentCanceled'), {
                    title: this.$t('success'),
                    variant: 'success',
                })
                // Fetch fresh spark data to get updated statuses
                return this.fetchSparkStatus()
            })
            .then(() => {
                // Stop polling since deployment is canceled
                this.stopPolling()
            })
            .catch(err => {
                console.error('Full error object:', err)
                console.error('Error response:', err.response)
                console.error('Error response data:', err.response && err.response.data)

                let errorMessage = this.$t('unexpectedError')
                if (err.response && err.response.data) {
                    if (err.response.data.message) {
                        errorMessage = err.response.data.message
                    } else if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data
                    }
                } else if (err.message) {
                    errorMessage = err.message
                }

                this.$bvToast.toast(errorMessage, {
                    title: this.$t('error'),
                    variant: 'danger',
                    autoHideDelay: 5000
                })
            }).finally(() => {
                popup.hide()
            })
        },

        forkVersion() {
            let settings = this.spark.settings.find(s => {
                return s.version == this.previewVersion
            })

            let settingsToFork = cloneDeep(settings)
            settingsToFork.version = this.forkTo

            this.spark.settings.push(settingsToFork)

            this.save()
        },

        addEnvVariable() {
            if (!this.spark.info[this.env].env_vars) {
                this.$set(this.spark.info[this.env], 'env_vars', {})
            }
            
            // Generate a unique key for new variable
            let counter = 1
            let newKey = `NEW_VAR_${counter}`
            while (this.spark.info[this.env].env_vars[newKey]) {
                counter++
                newKey = `NEW_VAR_${counter}`
            }
            
            this.$set(this.spark.info[this.env].env_vars, newKey, '')
        },

        removeEnvVariable(index) {
            const variable = this.envVariablesList[index]
            this.$delete(this.spark.info[this.env].env_vars, variable.key)
        },

        updateEnvVariables() {
            // Rebuild the env_vars object from the list
            const newEnvVars = {}
            this.envVariablesList.forEach(variable => {
                if (variable.key && variable.key.trim() !== '') {
                    newEnvVars[variable.key] = variable.value
                }
            })
            this.$set(this.spark.info[this.env], 'env_vars', newEnvVars)
        },

        save() {
            let popup = this.$loadingPopup()
            this.$sandbox.spark().update(this.$route.params.id, this.spark).finally(() => {
                popup.hide()
            })
        },

        async remove() {
            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return

            const popup = this.$loadingPopup()
            this.$sandbox.spark().delete(this.$route.params.id).then(() => {
                this.$router.push("/admin/spark")
            }).catch((err) => {
                console.error(err)

                this.$bvToast.toast(this.$t("unexpectedError"), {
                    title: this.$t("error"),
                    variant: "danger",
                })
            }).finally(() => {
                popup.hide()
            })
        },

        isReady(env) {
            return this.spark.info[env].status != ""
        },

        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                this.$bvToast.toast('Endpoint copied to clipboard', {
                    title: 'Success',
                    variant: 'success',
                    autoHideDelay: 2000
                })
            }).catch(() => {
                this.$bvToast.toast('Failed to copy', {
                    title: 'Error',
                    variant: 'danger'
                })
            })
        },

        testEndpoint(endpoint) {
            this.testEndpointUrl = endpoint
            this.testMethod = 'POST'
            this.testRequestBody = JSON.stringify({
                // Example request body
            }, null, 2)
            this.testResponse = null
            this.testError = null
            this.testModal = true
        },

        async executeTest() {
            this.testLoading = true
            this.testError = null
            this.testResponse = null

            try {
                const config = {
                    method: this.testMethod,
                    url: this.testEndpointUrl,
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }

                if (this.testMethod !== 'GET' && this.testRequestBody) {
                    config.data = JSON.parse(this.testRequestBody)
                }

                const response = await this.$http(config)

                this.testResponse = {
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                    data: response.data
                }
            } catch (error) {
                if (error.response) {
                    this.testResponse = {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        headers: error.response.headers,
                        data: error.response.data
                    }
                } else {
                    this.testError = error.message || 'Failed to execute request'
                }
            } finally {
                this.testLoading = false
            }
        },

        startPolling() {
            console.log('Starting polling...')
            // Poll every 10 seconds if building or deploying
            this.pollInterval = setInterval(() => {
                console.log('Polling check - isBuilding:', this.isBuilding, 'devStatus:', this.devStatus, 'liveStatus:', this.liveStatus)
                if (this.isBuilding) {
                    this.fetchSparkStatus()
                } else {
                    // Stop polling when deployment is complete (status is 'ready' or 'failed')
                    console.log('Stopping polling - deployment complete')
                    this.stopPolling()
                }
            }, 10000)
        },

        stopPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval)
                this.pollInterval = null
            }
        },

        fetchSparkStatus() {
            console.log('Fetching spark status...')
            this.$sandbox.spark().get(this.$route.params.id).then(resp => {
                console.log('Spark status response:', resp.data)
                console.log('Dev status:', resp.data.info && resp.data.info.dev && resp.data.info.dev.status)
                console.log('Live status:', resp.data.info && resp.data.info.live && resp.data.info.live.status)
                
                this.spark = Object.assign({}, resp.data, {
                    memory: resp.data.info.live.memory
                })
            }).catch(err => {
                console.error('Error fetching spark status:', err)
                this.$debugLog(err)
            })
        },

        onTabChange(tabIndex) {
            this.activeTabIndex = tabIndex
            // Tab index 3 is the Logs tab (0: General, 1: Code, 2: Environment, 3: Logs)
            if (tabIndex === 3) {
                this.startLogPolling()
            } else {
                this.stopLogPolling()
            }
        },

        startLogPolling() {
            if (this.logPollingActive) return
            
            this.logPollingActive = true
            // Set 'since' to now - 5 seconds using ISO format
            const fiveSecondsAgo = new Date(Date.now() - 5000)
            this.logSince = fiveSecondsAgo.toISOString()
            
            // Don't fetch immediately - wait for first poll interval
            // Poll every 10 seconds
            this.logInterval = setInterval(() => {
                this.fetchLogs()
            }, 10000)
        },

        stopLogPolling() {
            this.logPollingActive = false
            if (this.logInterval) {
                clearInterval(this.logInterval)
                this.logInterval = null
            }
        },

        toggleLogPolling() {
            if (this.logPollingActive) {
                this.stopLogPolling()
            } else {
                this.startLogPolling()
            }
        },

        switchLogEnv(env) {
            this.logEnv = env
            this.clearLogs()
            if (this.logPollingActive) {
                this.stopLogPolling()
                this.startLogPolling()
            }
        },

        async fetchLogs() {
            try {
                const params = {
                    env: this.logEnv
                }
                
                // Always use logSince (now - 5s) for polling
                if (this.logSince) {
                    params.since = this.logSince
                }

                const response = await this.$sandbox.spark().logs(
                    this.$route.params.id,
                    params
                )

                if (response.data && response.data.logs && response.data.logs.length > 0) {
                    // Parse logs (they're now objects, not strings)
                    const parsedLogs = response.data.logs.map(log => this.parseLogString(log))
                    
                    // Prepend new logs (newest first)
                    this.logs.unshift(...parsedLogs.reverse())
                    
                    // Limit logs to last 1000 entries to prevent memory issues
                    if (this.logs.length > 1000) {
                        this.logs = this.logs.slice(0, 1000)
                    }
                }
                
                // Always update 'since' to now - 5 seconds for next poll
                const fiveSecondsAgo = new Date(Date.now() - 5000)
                this.logSince = fiveSecondsAgo.toISOString()
            } catch (err) {
                console.error('Error fetching logs:', err)
                // Don't show error toast for log polling failures
            }
        },

        clearLogs() {
            this.logs = []
            this.manualSince = null
            // Set 'since' to now - 5 seconds using ISO format
            const fiveSecondsAgo = new Date(Date.now() - 5000)
            this.logSince = fiveSecondsAgo.toISOString()
        },

        async onManualSinceChange() {
            // When manual since is changed, clear logs and fetch once from that time
            this.logs = []
            
            try {
                const params = {
                    env: this.logEnv,
                    since: new Date(this.manualSince).toISOString()
                }

                const response = await this.$sandbox.spark().logs(
                    this.$route.params.id,
                    params
                )

                if (response.data && response.data.logs && response.data.logs.length > 0) {
                    // Parse logs (they're now objects, not strings)
                    const parsedLogs = response.data.logs.map(log => this.parseLogString(log))
                    
                    // Set logs (newest first)
                    this.logs = parsedLogs.reverse()
                    
                    // Limit logs to last 1000 entries
                    if (this.logs.length > 1000) {
                        this.logs = this.logs.slice(0, 1000)
                    }
                }
            } catch (err) {
                console.error('Error fetching manual logs:', err)
            }
            
            // Reset logSince to now - 5s for regular polling to continue
            const fiveSecondsAgo = new Date(Date.now() - 5000)
            this.logSince = fiveSecondsAgo.toISOString()
        },

        resetManualSince() {
            this.manualSince = null
            this.logs = []
            const fiveSecondsAgo = new Date(Date.now() - 5000)
            this.logSince = fiveSecondsAgo.toISOString()
            this.fetchLogs()
        },

        getCurrentDateTime() {
            // Return current datetime in format required by datetime-local input
            const now = new Date()
            const year = now.getFullYear()
            const month = String(now.getMonth() + 1).padStart(2, '0')
            const day = String(now.getDate()).padStart(2, '0')
            const hours = String(now.getHours()).padStart(2, '0')
            const minutes = String(now.getMinutes()).padStart(2, '0')
            return `${year}-${month}-${day}T${hours}:${minutes}`
        },

        parseLogString(log) {
            // New format is already a JSON object with message, level, timestamp, and optional data
            if (typeof log === 'object' && log !== null) {
                return {
                    timestamp: log.timestamp,
                    level: log.level || 'info',
                    message: log.message || '',
                    data: log.data || null
                }
            }
            
            // Legacy string format fallback: "2026-01-13 08:58:07 [INFO]: Server listening on port 8080"
            if (typeof log === 'string') {
                const regex = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[([A-Z]+)\]: (.*)$/
                const match = log.match(regex)
                
                if (match) {
                    return {
                        timestamp: match[1],
                        level: match[2].toLowerCase(),
                        message: match[3],
                        data: null
                    }
                }
            }
            
            // Fallback for non-standard format
            return {
                timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
                level: 'info',
                message: String(log),
                data: null
            }
        },

        inspectLog(log) {
            this.selectedLog = log
            this.inspectModal = true
        },

        formatTimestamp(timestamp) {
            if (!timestamp) return ''
            // Handle "2026-01-13 08:58:07" format or ISO 8601
            return timestamp.substring(11, 19) || timestamp
        },

        getSeverityClass(severity) {
            if (!severity) return 'default'
            const severityLower = severity.toLowerCase()
            if (severityLower === 'error') return 'error'
            if (severityLower === 'warning') return 'warn'
            if (severityLower === 'info') return 'info'
            if (severityLower === 'debug') return 'debug'
            return 'default'
        }
    },

    destroy() {
        clearInterval(this.logInterval)
        this.logInterval = null
        this.stopPolling()
        this.stopLogPolling()
    },

    beforeDestroy() {
        this.stopPolling()
        this.stopLogPolling()
    },

    watch: {
        isBuilding(newVal) {
            if (newVal && !this.pollInterval) {
                this.startPolling()
            } else if (!newVal && this.pollInterval) {
                this.stopPolling()
            }
        }
    },

    mounted() {
       this.$sandbox.spark().get(this.$route.params.id).then(resp => {
            this.spark = Object.assign({}, resp.data, {
                memory: resp.data.info.live.memory
            })
            this.ideVersion = this.spark.dev_version || (this.availableVersion.length > 0 ? this.availableVersion[0] : this.$t('choose'))
            
            // Initialize previous memory
            this.previousMemory = this.spark.memory
            
            // Start polling if building
            if (this.isBuilding) {
                this.startPolling()
            }
       }).catch(err => {
           this.$debugLog(err)
           this.$mainDialog.msgBoxOk("Failed to fetch spark.")
       })
    }
}
</script>

<style scoped>
/* Main Container */
.spark-page {
    min-height: 100vh;
    padding: 1.5rem;
    background: var(--bg-main-start);
}

/* Loading State */
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    color: var(--text-secondary);
}

/* Page Header */
.page-header {
    background: var(--bg-card);
    padding: 2rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-separator);
    box-shadow: var(--shadow-sm);
}

.page-header h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
}

.page-header .text-muted {
    color: var(--text-secondary) !important;
}

/* Tabs */
.spark-content >>> .spark-tabs {
    background: var(--bg-card);
    padding: 0.5rem 1rem 0;
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    border: 1px solid var(--border-separator);
    border-bottom: none;
}

.spark-content >>> .nav-tabs .nav-link {
    color: var(--text-secondary);
    border: none;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
    transition: var(--transition-quick);
}

.spark-content >>> .nav-tabs .nav-link:hover {
    color: var(--text-primary);
    background: var(--bg-card-secondary);
    border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.spark-content >>> .nav-tabs .nav-link.active {
    color: var(--primary-start);
    background: var(--bg-card);
    border: none;
    border-bottom: 2px solid var(--primary-start);
    font-weight: 600;
}

/* Cards */
.config-card,
.version-card,
.code-card,
.env-card,
.env-selector-card,
.endpoints-card {
    background: var(--bg-card);
    border: 1px solid var(--border-separator);
    border-radius: var(--radius-md);
    overflow: visible;
    transition: var(--transition-quick);
}

.config-card:hover,
.version-card:hover,
.code-card:hover,
.env-card:hover,
.endpoints-card:hover {
    border-color: var(--border-default);
    box-shadow: var(--shadow-md) !important;
}

.config-card >>> .card-header,
.version-card >>> .card-header,
.code-card >>> .card-header,
.env-card >>> .card-header,
.endpoints-card >>> .card-header {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--border-separator);
    font-weight: 600;
}

.env-selector-card >>> .card-body {
    padding: 1rem 1.25rem;
}

.config-card >>> .card-body,
.version-card >>> .card-body,
.code-card >>> .card-body,
.env-card >>> .card-body,
.endpoints-card >>> .card-body {
    padding: 1.5rem;
    background: var(--bg-card);
}

.version-card >>> .card-footer {
    background: var(--bg-card-secondary);
    border-top: 1px solid var(--border-separator);
    padding: 1rem 1.25rem;
}

/* Form Elements */
.font-weight-semibold {
    font-weight: 600;
    color: var(--text-secondary);
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.version-select,
.custom-v-select >>> .vs__dropdown-toggle {
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-default);
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    transition: var(--transition-quick);
}

.version-select:focus,
.custom-v-select >>> .vs__dropdown-toggle:focus {
    border-color: var(--border-hover);
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
    background: var(--bg-card-secondary);
}

.version-select option {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
}

/* Version Badges */
.version-badge {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    white-space: nowrap;
}

/* Buttons */
.save-btn {
    font-weight: 600;
    padding: 0.75rem;
    border-radius: var(--radius-sm);
    transition: var(--transition-quick);
}

.save-btn:hover {
    box-shadow: var(--shadow-success);
}

.env-toggle {
    font-weight: 500;
    padding: 0.75rem;
    transition: var(--transition-quick);
    background: var(--bg-card-secondary);
    border-color: var(--border-default);
    color: var(--text-secondary);
}

.env-toggle:hover {
    background: var(--bg-card);
    border-color: var(--border-hover);
    color: var(--text-primary);
}

.btn-outline-primary.env-toggle {
    color: var(--text-secondary);
    border-color: var(--border-default);
}

.btn-primary.env-toggle {
    background: var(--primary-start);
    border-color: var(--primary-start);
    color: var(--text-on-button);
}

/* Code Editor Container */
.code-card >>> .CodeMirror {
    border-radius: var(--radius-sm);
    font-size: 14px;
    background: var(--bg-card-secondary);
    color: var(--text-primary);
}

/* Custom v-select styling */
.custom-v-select >>> .vs__dropdown-toggle {
    padding: 0.5rem;
    background: var(--bg-card-secondary);
}

.custom-v-select >>> .vs__selected {
    margin: 0;
    padding: 0.25rem 0.5rem;
    background: var(--primary-start);
    color: var(--text-on-button);
    border-radius: 4px;
}

.custom-v-select >>> .vs__search {
    padding: 0.25rem 0.5rem;
    color: var(--text-primary);
}

.custom-v-select >>> .vs__search::placeholder {
    color: var(--text-muted);
}

.custom-v-select >>> .vs__actions {
    padding: 0 0.5rem;
}

.custom-v-select >>> .vs__dropdown-menu {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    color: var(--text-primary);
}

.custom-v-select >>> .vs__dropdown-option {
    color: var(--text-primary);
}

.custom-v-select >>> .vs__dropdown-option--highlight {
    background: var(--bg-card);
    color: var(--text-primary);
}

/* Responsive Adjustments */
@media (max-width: 991px) {
    .page-header h2 {
        font-size: 1.5rem;
    }
    
    .spark-page {
        padding: 1rem;
    }
}

/* Terminal Log Styles */
.terminalLog {
    background: var(--bg-card-secondary);
    color: #00ff00;
    font-size: 14px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    max-height: 400px;
    overflow-y: auto;
    padding: 1rem;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-separator);
}

.terminalLog ul {
    list-style-type: "→";
    padding-left: 1.5rem;
}

.terminalLog ul > li {
    padding: 0.25rem 0;
    line-height: 1.5;
}

/* Scrollbar Styling */
.terminalLog::-webkit-scrollbar {
    width: 8px;
}

.terminalLog::-webkit-scrollbar-track {
    background: var(--bg-card);
    border-radius: 4px;
}

.terminalLog::-webkit-scrollbar-thumb {
    background: var(--border-hover);
    border-radius: 4px;
}

.terminalLog::-webkit-scrollbar-thumb:hover {
    background: var(--border-active);
}

/* Modal Enhancements */
>>> .modal-content {
    background: var(--bg-card);
    border: 1px solid var(--border-separator);
}

>>> .modal-header {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    border-bottom: 1px solid var(--border-separator);
}

>>> .modal-header .close {
    color: var(--text-primary);
}

>>> .modal-body {
    background: var(--bg-card);
    color: var(--text-primary);
}

>>> .modal-body .form-control {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    color: var(--text-primary);
}

>>> .modal-body .form-control:focus {
    background: var(--bg-card-secondary);
    border-color: var(--border-hover);
    color: var(--text-primary);
}

>>> .modal-body .form-control:disabled,
>>> .modal-body .form-control.bg-light {
    background: var(--bg-card-secondary);
    opacity: 0.6;
}

>>> .modal-body label {
    color: var(--text-secondary);
}

>>> .modal-footer {
    background: var(--bg-card);
    border-top: 1px solid var(--border-separator);
    padding: 1rem 1.25rem;
}

/* Animation */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.spark-content {
    animation: fadeIn 0.3s ease;
}

/* Additional Dark Theme Adjustments */
>>> .form-control,
>>> .form-select,
>>> input.form-control {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    color: var(--text-primary);
}

>>> .form-control:focus,
>>> .form-select:focus {
    background: var(--bg-card-secondary);
    border-color: var(--border-hover);
    color: var(--text-primary);
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

>>> .form-control::placeholder {
    color: var(--text-muted);
}

>>> .input-group-text {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    color: var(--text-secondary);
}

>>> .dropdown-menu {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
}

>>> .dropdown-item {
    color: var(--text-primary);
}

>>> .dropdown-item:hover {
    background: var(--bg-card);
    color: var(--text-primary);
}

/* Endpoint Input */
.endpoint-input {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.875rem;
}

/* Status Badge */
.status-badge {
    font-size: 0.75rem;
    padding: 0.35rem 0.75rem;
    font-weight: 600;
}

.pulse-badge {
    animation: pulse 2s infinite;
}

.clickable-badge {
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
}

.clickable-badge:hover {
    opacity: 0.85;
    transform: scale(1.05);
}

.clickable-badge:active {
    transform: scale(0.98);
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}

>>> .alert-secondary {
    background: var(--bg-card-secondary);
    border-color: var(--border-default);
    color: var(--text-muted);
}

/* Test Modal */
.test-select {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    color: var(--text-primary);
}

.test-error {
    margin-bottom: 1rem;
}

.test-response {
    background: var(--bg-card);
    padding: 1rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-separator);
}

.test-placeholder {
    background: var(--bg-card-secondary);
    border-radius: var(--radius-md);
    border: 2px dashed var(--border-default);
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.status-code-badge {
    font-size: 1rem;
    padding: 0.5rem 1rem;
    font-weight: 600;
}

>>> .modal-xl .modal-body {
    background: var(--bg-card);
}

>>> .modal-xl .modal-header {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    border-bottom: 1px solid var(--border-separator);
}

/* Environment Variables Key-Value Editor */
.env-variables-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.env-variable-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 0.75rem;
    align-items: center;
    padding: 0.75rem;
    background: var(--bg-card-secondary);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-default);
    transition: var(--transition-quick);
}

.env-variable-row:hover {
    border-color: var(--border-hover);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.env-key-group,
.env-value-group {
    margin-bottom: 0;
}

.env-input {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    color: var(--text-primary);
    border-radius: var(--radius-sm);
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    transition: var(--transition-quick);
}

.env-input:focus {
    border-color: var(--border-hover);
    background: var(--bg-card);
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
}

.env-input::placeholder {
    color: var(--text-muted);
    opacity: 0.6;
}

.env-delete-btn {
    padding: 0.5rem 0.75rem;
    border-radius: var(--radius-sm);
    transition: var(--transition-quick);
}

.env-delete-btn:hover {
    transform: scale(1.05);
}

@media (max-width: 768px) {
    .env-variable-row {
        grid-template-columns: 1fr;
        gap: 0.5rem;
    }
    
    .env-delete-btn {
        justify-self: end;
    }
}

/* Logs Styles */
.logs-card {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
}

/* Date Picker Wrapper */
.date-picker-wrapper {
    min-width: 280px;
}

.date-picker-wrapper input[type="datetime-local"] {
    background: var(--bg-card-secondary);
    border-color: var(--border-default);
    color: var(--text-primary);
    font-size: 0.875rem;
}

.date-picker-wrapper input[type="datetime-local"]:focus {
    background: var(--bg-card-secondary);
    border-color: var(--primary-start);
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.date-picker-wrapper input[type="datetime-local"]::-webkit-calendar-picker-indicator {
    filter: invert(0.8);
    cursor: pointer;
}

/* Log Search Wrapper */
.log-search-wrapper {
    min-width: 300px;
}

.log-search-wrapper input {
    background: var(--bg-card-secondary);
    border-color: var(--border-default);
    color: var(--text-primary);
    font-size: 0.875rem;
}

.log-search-wrapper input:focus {
    background: var(--bg-card-secondary);
    border-color: var(--primary-start);
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.log-search-wrapper input::placeholder {
    color: var(--text-muted);
}

.logs-container {
    min-height: 500px;
    max-height: 700px;
    overflow-y: auto;
    background: #1e1e1e;
    border-radius: var(--radius-sm);
    padding: 1rem;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
    font-size: 0.875rem;
    line-height: 1.6;
}

.logs-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.log-entry {
    display: flex;
    gap: 1rem;
    padding: 0.5rem;
    border-radius: 4px;
    transition: background 0.2s;
}

.log-entry:hover {
    background: rgba(255, 255, 255, 0.05);
}

.log-entry.log-clickable {
    cursor: pointer;
}

.log-entry.log-clickable:hover {
    background: rgba(102, 126, 234, 0.15);
}

.log-entry.log-clickable:active {
    background: rgba(102, 126, 234, 0.25);
}

.log-timestamp {
    color: #6b7280;
    flex-shrink: 0;
    min-width: 90px;
    font-weight: 500;
}

.log-level {
    flex-shrink: 0;
    min-width: 60px;
    font-weight: 600;
    text-transform: uppercase;
}

.log-message {
    color: #e5e7eb;
    word-break: break-word;
    flex: 1;
}

.log-level-info .log-level {
    color: #3b82f6;
}

.log-level-warn .log-level,
.log-level-warning .log-level {
    color: #f59e0b;
}

.log-level-error .log-level {
    color: #ef4444;
}

.log-level-debug .log-level {
    color: #8b5cf6;
}

.log-level-success .log-level {
    color: #10b981;
}

/* Scrollbar styling for logs */
.logs-container::-webkit-scrollbar {
    width: 8px;
}

.logs-container::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
}

.logs-container::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
}

.logs-container::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
}

/* ── Version Pipeline ───────────────────────────── */
.sp-versions {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-default);
    margin-bottom: 16px;
    overflow-x: auto;
    scrollbar-width: none;
}
.sp-versions::-webkit-scrollbar {
    display: none;
}

.sp-version-stage {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.sp-version-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-muted, #949ba4);
    white-space: nowrap;
}

.sp-version-select {
    width: auto;
    min-width: 72px;
    max-width: 110px;
    font-size: 12px;
    padding: 2px 24px 2px 8px;
    height: 28px;
}

.sp-version-action {
    padding: 2px 4px;
    color: var(--text-secondary);
    line-height: 1;
}

.sp-version-promote {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.sp-version-arrow {
    color: var(--text-muted, #949ba4);
    font-size: 11px;
    opacity: 0.6;
}

.sp-version-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.3;
}

.sp-version-chip-icon {
    font-size: 14px;
}

.sp-version-chip--success {
    background: rgba(72, 187, 120, 0.12);
    color: var(--success-start, #48bb78);
    border: 1px solid rgba(72, 187, 120, 0.25);
}

.sp-version-chip--live {
    background: rgba(108, 128, 255, 0.12);
    color: var(--primary-start, #6c80ff);
    border: 1px solid rgba(108, 128, 255, 0.25);
}

.sp-version-chip--inactive {
    background: var(--bg-card-secondary, rgba(255, 255, 255, 0.05));
    color: var(--text-muted, #949ba4);
    border: 1px solid var(--border-default);
}
</style>